import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Box,
  Flex,
  Text,
  Button,
  VStack,
  HStack,
  Icon,
  Grid,
  GridItem,
  IconButton,
} from "@chakra-ui/react";
import { FiArrowLeft, FiMap } from "react-icons/fi";
import {
  getMeetingTypeIcon,
  getGoalIcon,
  getPropertyIcon,
  formatDuration,
} from "@utils/blueprintIcons";
import smartAgendasContent from "@constants/smartAgendasContent";

/**
 * BlueprintModal Component
 * Displays agenda blueprint information based on Figma design
 * Shows meeting type, goal, audience, style, focus area, and duration
 */
const BlueprintModal = ({
  isOpen = false,
  onClose,
  blueprint = {},
  onRemix,
  isPastAgenda = false,
}) => {
  if (!isOpen || !blueprint) return null;

  /**
   * Get display data for blueprint properties from smartAgendasContent.json
   */
  const getBlueprintDisplayData = () => {
    const {
      meetingType,
      goal,
      audience,
      style,
      focusArea,
      duration,
      agendaTitle,
    } = blueprint;

    // Get meeting type data
    const meetingTypeData = smartAgendasContent.meetingTypes.find(
      (type) => type.id === meetingType
    );

    // Get goal data - check universal goals first, then meeting-type-specific goals
    const meetingTypeContent =
      smartAgendasContent.meetingTypeContent[meetingType];
    const goalData =
      smartAgendasContent.universalGoals?.find((g) => g.id === goal) ||
      meetingTypeContent?.goals?.find((g) => g.id === goal);

    // Get audience data
    const audienceData = meetingTypeContent?.audienceOptions?.find(
      (a) => a.value === audience
    );

    // Get style data
    const styleData = meetingTypeContent?.styleOptions?.find(
      (s) => s.value === style
    );

    return {
      meetingType: {
        title: meetingTypeData?.title || "Unknown Meeting Type",
        description: meetingTypeData?.description || "",
        icon: meetingTypeData?.icon || "FaQuestionCircle",
      },
      goal: {
        title: goalData?.title || "Unknown Goal",
        description: goalData?.description || "",
        icon: goalData?.icon || "FaQuestionCircle",
      },
      audience: {
        title: audienceData?.title || audience || "Unknown Audience",
        description: audienceData?.description || "",
      },
      style: {
        title: styleData?.title || style || "Unknown Style",
        description: styleData?.description || "",
      },
      focusArea: focusArea || "Not specified",
      duration: formatDuration(duration),
      agendaTitle: agendaTitle || "Untitled Agenda",
    };
  };

  const displayData = getBlueprintDisplayData();

  /**
   * Property Card Component
   */
  const PropertyCard = ({ icon: IconComponent, label, value, description }) => (
    <Box
      bg="gray.50"
      borderRadius="lg"
      p={2}
      minH="16"
      display="flex"
      alignItems="center"
      gap={2}
    >
      {/* Icon Container */}
      <Box
        w={10}
        h={10}
        bg="white"
        borderRadius="sm"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        <Icon as={IconComponent} boxSize={3.5} color="purple.500" />
      </Box>

      {/* Content */}
      <Box flex="1" minW="0">
        <Text
          fontSize="sm"
          fontWeight="400"
          color="gray.500"
          lineHeight="shorter"
          letterSpacing="-1%"
          fontFamily="Geist, sans-serif"
          mb={0.5}
          textTransform="uppercase"
        >
          {label}
        </Text>
        <Text
          fontSize="xs"
          fontWeight="400"
          color="gray.800"
          lineHeight="short"
          letterSpacing="-1%"
          fontFamily="Geist, sans-serif"
          noOfLines={2}
        >
          {value}
        </Text>
      </Box>
    </Box>
  );

  /**
   * Main Card Component
   */
  const MainCard = ({ icon: IconComponent, title, description }) => (
    <Box
      bg="gray.50"
      borderRadius="lg"
      p={2}
      display="flex"
      alignItems="center"
      gap={3}
    >
      {/* Icon Container */}
      <Box
        w={10}
        h={10}
        bg="white"
        borderRadius="sm"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        <Icon as={IconComponent} boxSize={3.5} color="purple.500" />
      </Box>

      {/* Content */}
      <Box flex="1" minW="0">
        <Text
          fontSize="sm"
          fontWeight="600"
          color="gray.800"
          lineHeight="shorter"
          letterSpacing="-1%"
          fontFamily="Geist, sans-serif"
          mb={0.5}
        >
          {title}
        </Text>
        <Text
          fontSize="xs"
          fontWeight="400"
          color="gray.500"
          lineHeight="short"
          letterSpacing="-1%"
          fontFamily="Geist, sans-serif"
        >
          {description}
        </Text>
      </Box>
    </Box>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        bg="white"
        borderRadius="xl"
        maxW="420px"
        mx={4}
        position="relative"
      >
        <ModalBody p="0">
          <VStack spacing={4} p={5} align="stretch">
            {/* Header */}
            <HStack spacing={3} align="center">
              <IconButton
                aria-label="Go back"
                icon={<FiArrowLeft />}
                variant="ghost"
                size="sm"
                color="purple.500"
                onClick={onClose}
                _hover={{ bg: "gray.50" }}
              />
              <HStack spacing={2} flex="1">
                <Icon as={FiMap} boxSize={5} color="purple.500" />
                <Text
                  fontSize="md"
                  fontWeight="600"
                  color="gray.800"
                  lineHeight="short"
                  letterSpacing="-1%"
                  fontFamily="Geist, sans-serif"
                >
                  Agenda Blueprint
                </Text>
              </HStack>
            </HStack>

            {/* Subtitle */}
            <Text
              fontSize="xs"
              fontWeight="400"
              color="gray.500"
              lineHeight="short"
              letterSpacing="-1%"
              fontFamily="Geist, sans-serif"
            >
              This Smart Agenda is based on the below inputs.
            </Text>

            {/* Agenda Title */}
            <Box>
              <Text
                fontSize="md"
                fontWeight="700"
                color="gray.800"
                lineHeight="short"
                letterSpacing="-4%"
                fontFamily="Geist, sans-serif"
              >
                {displayData.agendaTitle}
              </Text>
            </Box>

            {/* Meeting Type Card */}
            <MainCard
              icon={getMeetingTypeIcon(displayData.meetingType.icon)}
              title={displayData.meetingType.title}
              description={displayData.meetingType.description}
            />

            {/* Goal Card */}
            <MainCard
              icon={getGoalIcon(displayData.goal.icon)}
              title={displayData.goal.title}
              description={displayData.goal.description}
            />

            {/* Properties Grid */}
            <Grid templateColumns="1fr 1fr" gap={2.5} w="100%">
              {/* Left Column */}
              <GridItem>
                <VStack spacing={3} align="stretch">
                  <PropertyCard
                    icon={getPropertyIcon("audience")}
                    label="Audience"
                    value={displayData.audience.title}
                  />
                  <PropertyCard
                    icon={getPropertyIcon("style")}
                    label="Style"
                    value={displayData.style.title}
                  />
                </VStack>
              </GridItem>

              {/* Right Column */}
              <GridItem>
                <VStack spacing={3} align="stretch">
                  <PropertyCard
                    icon={getPropertyIcon("focusArea")}
                    label="Focus Area"
                    value={displayData.focusArea}
                  />
                  <PropertyCard
                    icon={getPropertyIcon("duration")}
                    label="Duration"
                    value={displayData.duration}
                  />
                </VStack>
              </GridItem>
            </Grid>

            {/* Remix Button */}
            {!isPastAgenda && (
              <Button
                bg="purple.500"
                color="white"
                fontSize="sm"
                fontWeight="600"
                borderRadius="md"
                h={12}
                leftIcon={<Text>✨</Text>}
                _hover={{ bg: "purple.600" }}
                _active={{ bg: "purple.600", transform: "scale(0.98)" }}
                onClick={() => {
                  onRemix && onRemix(blueprint);
                  onClose();
                }}
              >
                Remix Agenda
              </Button>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default BlueprintModal;
